import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";
//--------------------------------------------------------------------------------------------
import UserInfo from "src/components/common/userInfo";
import { testerEmails } from "src/components/common/functions";
import { Lead } from "./types";
import { useAuthStore, User } from "../main/authStore";
//--------------------------------------------------------------------------------------------
type FieldDef = {
    label: string;
    value?: React.ReactNode;
    show?: boolean;
};

export default function LeadDetail() {
    const user = useAuthStore(state => state.user) as User;
    const lead = useRouteLoaderData<Lead>("lead")!;
    const submit = useSubmit();
    const handleDelete = async (id: number) => {
        if(!window.confirm("Are you sure to delete this lead?\n\nThis action cannot be undone.")) return;
        const formData = new FormData();
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Delete"
        });
    };
    const handleValidate = (id:number) => {
        if(!window.confirm("Are you sure to validate this lead?")) return;
        let validateReason = window.prompt("reason to validdate:");
        if(validateReason === null) return;
        while(validateReason === ""){
            window.alert("Reason is required to validate.");
            validateReason = window.prompt("reason to validate:");
            if(validateReason === null) return;
        }
        const formData = new FormData();
        formData.append("validateReason", validateReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Validate"
        });
    }
    const handleVoid = (id: number) => {
        if(!window.confirm("Are you sure to void this lead?")) return;
        let voidReason = window.prompt("reason to void:");
        if(voidReason === null) return;
        while(voidReason === ""){
            window.alert("Reason is required to void.");
            voidReason = window.prompt("reason to void:");
            if(voidReason === null) return;
        }
        const formData = new FormData();
        formData.append("voidReason", voidReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Void"
        });
    }
    const address = [ lead.street, lead.city, lead.state, lead.zipCode ].filter(Boolean).join(", ");
    const fields: FieldDef[] = [
        { label: "Lead Id", value: lead.id },
        { label: "Lead Name", value: lead.name },
        { label: "Organization Name", value: <Link to = {`/Organizations/${lead.organizationId}`}>{lead.organizationName}</Link> },
        { label: "Role", value: lead.role },
        { label: "Business Phone", value: lead.businessPhone?
            <a href = {`tel:+1${lead.businessPhone.replace(/-/g, "")}${lead.extension?`,${lead.extension}`:""}`}>
                {lead.businessPhone}{lead.extension?` Ext. ${lead.extension}`:null}
            </a>
            :null      
        },
        { label: "Fax", value: lead.fax?
            <a href = {"fax:+1" + lead.fax.replace(/-/g, "")}>{lead.fax}</a>
            :null
        },
        { label: "Mobile Number", value: lead.mobile?
            <a href = {`tel:+1${lead.mobile.replace(/-/g, "")}${lead.extension?`,${lead.extension}`:""}`}>
                {lead.mobile}
            </a>
            :null       
         },
        { label: "Email", value: lead.email && <a href = {`mailto:${lead.email}`} children = {lead.email}/> },
        { label: "Email Sent", value: Number(lead.sent) === 1 ? "Yes" : "No" },
        { label: "Real Email Sent", value: Number(lead.realSent) === 1 ? "Yes" : "No" },
        { label: "Address", value: address ? <a href = {`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target = "_blank" rel="noreferrer">{address}</a> : null},
        { label: "Oversea Address", value: lead.overseaAddress ? <a href = {`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lead.overseaAddress)}`} target = "_blank" rel="noreferrer">{lead.overseaAddress}</a> : null},
        

        {
            label: "Website",
            value: lead.website ? (
                <a
                    href = {lead.website}
                    target = "_blank"
                    rel = "noreferrer"
                >
                    {lead.website}
                </a>
            ) : null
        },
        { label: "Industry", value: lead.industry },
        { label: "Source", value: lead.source },
        { label: "Status", value: lead.status },
        {
            label: "User Responsible 1",
            show: !!lead.userResponsible1,
            value: <UserInfo
                        name={lead.userResponsible1Name}
                        id={lead.userResponsible1}
                    />
        }, 
        {
            label: "User Responsible 2",
            show: !!lead.userResponsible2,
            value: <UserInfo
                        name={lead.userResponsible2Name}
                        id={lead.userResponsible2}
                    />
        }, 
        {
            label: "Referred By",
            show: !!lead.referredBy,
            value: <UserInfo
                        name={lead.referredByName}
                        id={lead.referredBy}
                    />
        },


        {
            label: "Creator",
            show: !!lead.creatorId,
            value: (
                <>
                    <UserInfo
                        name={lead.creatorName}
                        id={lead.creatorId}
                    />
                    <span className="ms-2 text-muted">
                        {lead.createdAt}
                    </span>
                </>
            )
        },
        {
            label: "Updater",
            show: !!lead.updaterId,
            value: (
                <>
                    <UserInfo
                        name={lead.updaterName}
                        id={lead.updaterId}
                    />
                    <span className="ms-2 text-muted">
                        {lead.updatedAt}
                    </span>
                </>
            )
        },
        {
            label: "Void Reason",
            show: !!lead.voidReason,
            value: lead.voidReason
        },
        {
            label: "Validation Reason",
            show: !!lead.validateReason,
            value: lead.validateReason
        }
    ];
    // ------------------------------------------------------------------
    return (
        <div>
            <div className="cssSeperateWhenWide ps-3 pt-3">
                <table className="table table-fixed flex-grow-1 maxWidth-50 me-3">
                    <tbody className="noTableTopBorder">
                        {fields
                            .filter(f => f.value && f.show !== false)
                            .map((f, idx) => (
                                <tr key={idx}>
                                    <th>{f.label}</th>
                                    <td>{f.value}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
                <div className="d-flex flex-column flex-grow-1 maxWidth-50 me-3">
                    <fieldset className="border rounded flex-grow-1 px-3">
                        <legend className="fw-bold">
                            Background
                        </legend>
                        <span className = "cssNotes">
                            {lead.background}
                        </span>
                    </fieldset>
                </div>
            </div>
            <div className="my-3">
            </div>
            <div className="my-3">
                {lead.void === "yes"?<>
                    {testerEmails.includes(user.email)?
                    <button
                        children = "Validate"
                        className = "btn btn-primary"
                        onClick = {() => handleValidate(lead.id)}
                    />:null}
                </>:
                <>
                <Link
                    children = "Edit"
                    to = "Edit"
                    className = "btn btn-outline-primary ms-3"
                />
                <button
                    children = "Void"
                    type = "button"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleVoid(lead.id)}
                />
                </>}
                {lead.void === "yes"?
                <button
                    children = "Delete"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleDelete(lead.id)}
                />:null}
                <Link
                    children = "Back"
                    to = ".."
                    relative = "path"
                    className = "btn btn-outline-secondary float-end"
                />
            </div>
        </div>
    );
}
